import { X as XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

/**
 * Touch-friendly destructive-action confirmation dialog.
 * Identical visual language to the Restaurant "Ακύρωση & Επιστροφή" dialog so
 * users immediately recognise "this will delete something".
 *
 * Props:
 *  - open / onOpenChange  → standard Dialog props
 *  - title                → main heading (default "Επιβεβαίωση")
 *  - description          → secondary explanation (small, red)
 *  - subject              → optional bold subject (e.g. table name / customer)
 *  - rows                 → array of {label, value, accent?: "red"|"normal"}
 *  - confirmLabel         → text for the red action button (default "Ναι, διαγραφή")
 *  - cancelLabel          → text for the outline button (default "Όχι")
 *  - onConfirm            → async/sync handler — dialog auto-closes after
 *  - busy                 → disables both buttons while truthy
 *  - testidPrefix         → prefix for the data-testids (default "confirm")
 */
export default function ConfirmDialog({
  open,
  onOpenChange,
  title = "Επιβεβαίωση",
  description,
  subject,
  rows = [],
  confirmLabel = "Ναι, διαγραφή",
  cancelLabel = "Όχι",
  onConfirm,
  busy = false,
  testidPrefix = "confirm",
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideClose className="max-w-md p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200 bg-red-50">
          <DialogTitle className="text-2xl font-bold text-red-900 flex items-center gap-2">
            <XIcon size={22} className="text-red-600" />
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-sm text-red-800 mt-1">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="px-6 py-5 space-y-3">
          {subject && (
            <div className="text-base font-bold text-slate-800" data-testid={`${testidPrefix}-subject`}>
              {subject}
            </div>
          )}
          {rows.length > 0 && (
            <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-3 space-y-1">
              {rows.map((r, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between ${
                    r.accent === "red" ? "text-base mt-1" : "text-sm"
                  }`}
                >
                  <span
                    className={
                      r.accent === "red"
                        ? "text-slate-600 font-bold"
                        : "text-slate-600"
                    }
                  >
                    {r.label}
                  </span>
                  <span
                    className={`font-mono font-bold ${
                      r.accent === "red"
                        ? "text-red-700 text-lg"
                        : "text-slate-900"
                    }`}
                    data-testid={`${testidPrefix}-row-${i}`}
                  >
                    {r.value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="px-6 pb-5 pt-2 border-t border-slate-200 bg-slate-50 grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            className="h-12 text-base font-bold border-2"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid={`${testidPrefix}-no`}
          >
            {cancelLabel}
          </Button>
          <Button
            className="h-12 bg-red-600 hover:bg-red-700 text-white font-bold text-base"
            onClick={onConfirm}
            disabled={busy}
            data-testid={`${testidPrefix}-yes`}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
